import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CButton, CCol, CFormInput, CFormLabel, CRow } from "@coreui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBan,
  faEye,
  faRotateLeft,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";

import TNTable from "@/components/TNTable";
import ContentModerationTabs from "@/components/contentModeration/ContentModerationTabs";
import CustomDropdown from "@/components/CustomDropdown";
import DateFilterInput from "@/components/DateFilterInput";
import {
  showConfirmDialog,
  showDeactivateDialog,
} from "@/components/contentModeration/moderationDialogs";
import { SECTIONS } from "@/components/contentModeration/sections";
import { useTranslationContext } from "@/context/TranslationContext";
import { contentModeration as tr } from "@/constants/translation/contentModeration.translation";
import constants from "@/libs/constant";
import {
  useContentModerationList,
  useDeactivateContent,
  useDeleteContent,
  useReactivateContent,
} from "@/hooks/useContentModeration";

const STATUS_FILTER_VALUES = ["all", "active", "deactivated"];
const { dateTypes } = constants;

/**
 * Generic Content Moderation list page. Used by Groups, Communities, Activities and Feed Posts - the section
 * decides the columns (see components/contentModeration/sections.js), everything else is shared:
 * search, status filter, date range, pagination and the View / Deactivate / Reactivate / Delete actions.
 *
 * @param {Object} props
 * @param {string} props.sectionKey - groups | communities | activities | feedPosts
 */
const ContentModerationList = ({ sectionKey }) => {
  const t = useTranslationContext();
  const navigate = useNavigate();
  const section = SECTIONS[sectionKey];

  const [currentPage, setCurrentPage] = useState(
    constants.pagination.defaultPage,
  );
  const [searchText, setSearchText] = useState("");
  const [tempSearchText, setTempSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const searchTimeout = useRef(null);

  useEffect(
    () => () => {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
    },
    [],
  );

  const handleSearch = (e) => {
    const value = e.target.value;
    setTempSearchText(value);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setSearchText(value);
      setCurrentPage(1);
    }, 400);
  };

  const handleDateChange = (dateType, value) => {
    if (dateType === dateTypes.start) {
      setStartDate(value);
      // keep the range valid: the end can never be before the start
      if (value && endDate && endDate < value) setEndDate(value);
    } else {
      setEndDate(value);
    }
    setCurrentPage(1);
  };

  const hasFilter = Boolean(
    tempSearchText.trim() || statusFilter !== "all" || startDate || endDate,
  );

  const handleClearFilter = () => {
    setTempSearchText("");
    setSearchText("");
    setStatusFilter("all");
    setStartDate("");
    setEndDate("");
    setCurrentPage(constants.pagination.defaultPage);
  };

  const {
    data: listData,
    isLoading,
    isFetching,
  } = useContentModerationList(sectionKey, {
    page: currentPage || constants.pagination.defaultPage,
    limit: constants.pagination.defaultPageSize,
    ...(searchText.trim() ? { search: searchText.trim() } : {}),
    ...(statusFilter !== "all" ? { status: statusFilter } : {}),
    ...(startDate ? { date_from: startDate } : {}),
    ...(endDate ? { date_to: endDate } : {}),
  });

  const rows = listData?.data?.list || [];
  const paginationData = listData?.data?.pagination;

  // After deleting the last row of the last page, step back so the admin does not land on an empty page.
  useEffect(() => {
    const lastPage = Number(paginationData?.last_page || 0);
    if (!isFetching && lastPage > 0 && currentPage > lastPage)
      setCurrentPage(lastPage);
  }, [paginationData, currentPage, isFetching]);

  const onFailure = (error) =>
    toast.error(error?.response?.data?.message || t(tr.toast.failed));

  const { mutate: deactivate } = useDeactivateContent(
    sectionKey,
    (response) => toast.success(response?.message || t(tr.toast.deactivated)),
    onFailure,
  );
  const { mutate: reactivate } = useReactivateContent(
    sectionKey,
    (response) => toast.success(response?.message || t(tr.toast.reactivated)),
    onFailure,
  );
  const { mutate: remove } = useDeleteContent(
    sectionKey,
    (response) => toast.success(response?.message || t(tr.toast.deleted)),
    onFailure,
  );

  const handleView = (row) =>
    navigate(section.routes.view.replace(":id", row.uuid));

  const handleDeactivate = (row) =>
    showDeactivateDialog({
      t,
      onConfirm: (reason) => deactivate({ uuid: row.uuid, reason }),
    });

  const handleReactivate = (row) =>
    showConfirmDialog({
      t,
      texts: tr.popups.reactivate,
      onConfirm: () => reactivate(row.uuid),
    });

  const handleDelete = (row) =>
    showConfirmDialog({
      t,
      texts: tr.popups.delete,
      onConfirm: () => remove(row.uuid),
    });

  const statusFilterOptions = useMemo(
    () =>
      STATUS_FILTER_VALUES.map((value) => ({
        value,
        label: t(tr.list.status_filter_options[value]),
      })),
    [t],
  );

  const columns = useMemo(
    () => [
      ...section.columns(t),
      {
        id: "actions",
        accessorKey: "uuid",
        header: t(tr.list.columns.actions),
        cell: ({ row }) => {
          const isDeactivated = row.original.moderationStatus === "deactivated";
          return (
            <div className="d-flex gap-3 justify-content-center">
              <div
                className="table-action-text"
                title={t(tr.actions.view)}
                onClick={() => handleView(row.original)}
              >
                <FontAwesomeIcon icon={faEye} className="list-icon" />
              </div>
              {isDeactivated ? (
                <div
                  className="table-action-text"
                  title={t(tr.actions.reactivate)}
                  onClick={() => handleReactivate(row.original)}
                >
                  <FontAwesomeIcon icon={faRotateLeft} className="list-icon" />
                </div>
              ) : (
                <div
                  className="table-action-text"
                  title={t(tr.actions.deactivate)}
                  onClick={() => handleDeactivate(row.original)}
                >
                  <FontAwesomeIcon icon={faBan} className="list-icon" />
                </div>
              )}
              <div
                className="table-action-text"
                title={t(tr.actions.delete)}
                onClick={() => handleDelete(row.original)}
              >
                <FontAwesomeIcon icon={faTrash} className="list-icon" />
              </div>
            </div>
          );
        },
      },
    ],
    // the handlers only close over stable mutate functions and `t`
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, section],
  );

  return (
    <div className="content-moderation-list">
      <ContentModerationTabs />
      <CRow className="list-align">
        <CCol className="d-flex gap-2 justify-content-start justify-content-md-end flex-wrap align-items-center">
          <CFormInput
            placeholder={t(section.text.search)}
            type="text"
            value={tempSearchText}
            onChange={handleSearch}
            className="filter-field-space form-control-sm"
            style={{ maxWidth: "300px", height: "38px" }}
          />

          <div className="d-flex gap-2 align-items-center">
            <CFormLabel htmlFor="moderationStartDate" className="m-0">
              {t(tr.list.date_from)}
            </CFormLabel>
            <DateFilterInput
              id="moderationStartDate"
              value={startDate}
              placeholder="mm/dd/yyyy"
              onChange={(value) => handleDateChange(dateTypes.start, value)}
            />
          </div>

          <div className="d-flex gap-2 align-items-center">
            <CFormLabel htmlFor="moderationEndDate" className="m-0">
              {t(tr.list.date_to)}
            </CFormLabel>
            <DateFilterInput
              id="moderationEndDate"
              value={endDate}
              min={startDate}
              placeholder="mm/dd/yyyy"
              onChange={(value) => handleDateChange(dateTypes.end, value)}
            />
          </div>

          <CustomDropdown
            className="filter-field-space form-control-sm"
            style={{ maxWidth: "200px", height: "38px", padding: "0px" }}
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            options={statusFilterOptions}
          />

          {hasFilter && (
            <CButton
              color="primary"
              type="button"
              onClick={handleClearFilter}
              style={{ height: "38px" }}
            >
              {t(tr.list.clear_filter)}
            </CButton>
          )}
        </CCol>
      </CRow>

      <TNTable
        // TNTable keeps its own page index; remount on a filter change so it restarts at page 1
        // instead of staying on the previous (possibly last) page
        key={`${searchText}|${statusFilter}|${startDate}|${endDate}`}
        columns={columns}
        data={rows}
        paginationData={paginationData}
        onSelectPage={(pageIndex) => {
          setCurrentPage(pageIndex + 1);
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
        pageIndexGet={currentPage - 1}
        isLoading={isLoading}
        t={t}
        responsive={false}
        className="content-moderation-table"
      />
    </div>
  );
};

export default ContentModerationList;
